/**
 * @file 环境变量相关配置
 * @date 2021-06-24
 */

import { ram, ls } from '@sfe/bone-cache';

const { hostname } = window.location;

export const ROUTE_CHOSEN_STORAGE_KEY = 'route_chosen_v2';

/**
 * 当前域名对应的环境，会根据本地开发时的 `SFE_PROXY_ENV` 变量适配
 * - www.unify-test-1.bybit.com -> unify-test-1
 * - www.dev-1.bybit.com -> dev-1
 * - www.bybit.com -> prd
 * - m.bybit.com -> prd
 * - m-testnet.byapi.com -> testnet
 * - testnet.byapi.com -> testnet
 * - `SFE_PROXY_ENV`
 *      - prd -> prd
 *      - testnet -> testnet
 *      - unify-test-1 -> unify-test-1
 */
export const ENV = window.sfe.env;

/**
 * 当前是否开发环境
 */
export const IS_DEV = process.env.NODE_ENV !== 'production';

/**
 * 当前 Cookie 写入的域名，使用前需要评审
 */
export const COOKIE_MAIN_DOMAIN = (() => {
    // 目前现货项目只会在：`www.xxx` 、`testnet.xxx` 域名上运行，这里需要在运行的域名上获取主域，需要跟其它组逻辑一致
    let domain = window.location.hostname.replace(/^(www|m|testnet)/, '');

    // 适配本地开发时 `dev.bybit.com` 和 `dev.unify-test-1.com` 使用的上级域的 Cookie
    if (process.env.NODE_ENV !== 'production') domain = domain.replace(/^dev/, '');

    // 适配 SFE 前端自测环境使用的 `.bybit.com` 域名上的 Cookie
    // 因为 SFE 前端自测环境主要用于 testnet.xxx www.xxxx 的代理
    if (/\.sfe-test-1\.bybit\.com$/.test(domain)) domain = 'bybit.com';

    return domain;
})();

/**
 * 是否为 HTTPS
 */
export const isSSL = (() => {
    if (window.location.protocol === 'https:') return true;
    if (ENV === 'prd' || ENV === 'testnet') return true;
    return false;
})();

/**
 * 是否为 为正式环境
 */
export const isFormal = (() => {
    if (ENV === 'prd' || ENV === 'testnet') return true;
    return false;
})();

/**
 * 当前主域名
 * ---- 开发环境
 * - dev.unify-test-1.bybit.com -> bybit.com
 * - dev.unify-test-1.bybit.com.hk -> bybit.hk.com.hk
 * - dev.local.bybit.com -> bybit.com
 * - dev.local.bybit.hk.com -> bybit.com.hk
 * - dev.bybit.com -> bybit.com
 * - dev.bybit.com -> bybit.com.hk
 *
 * ---- 测试环境
 * - www.sbu-test-1.bybit.com -> bybit.com
 * - www.sbu-test-1.bybit.com.hk -> bybit.com.hk
 *
 * ---- testnet 环境
 * - testnet.bybit.com -> bybit.com
 * - testnet.bybit.com.hk -> bybit.com.hk
 *
 * ---- 生产环境
 * - www.bybit.com -> bybit.com
 * - www.bybit.com.hk -> bybit.com.hk
 * - `SFE_PROXY_ENV`
 *      - prd -> bybit.com || bybit.com.hk
 *      - testnet -> bybit.com || bybit.com.hk
 *      - sbu-test-1 -> bybit.com || bybit.com.hk
 */
export const CURRENT_DOMAIN = (() => {
    const hostnameSlice = (hn, index) => hn.split('.').slice(index).join('.');
    // 测试环境（包含本地开发测试环境） || 本地开发 testnet 环境
    if (ENV.includes('test-') || hostname.includes('dev.local')) {
        return hostnameSlice(hostname, 2);
    }
    // 线上 testnet 环境 || 线上预发布环境 || 生产环境（包含本地开发生产环境）
    return hostnameSlice(hostname, 1);
})();

// 当前环境对应域名列表
export const DOMAIN_LIST = (() => {
    const list = [];
    if (ENV === 'testnet') {
        list.push('bybitglobal.com');
    } else if (ENV === 'prd') {
        list.push('bycbe.com');
        list.push('byapis.com');
    } else {
        list.push('bybit-cn.com');
    }
    if (list.includes(CURRENT_DOMAIN)) return list;
    list.unshift(CURRENT_DOMAIN);
    return list;
})();

// 接口API 当前环境对应域名列表
// 参考 https://git.bybit.com/fe/bybit-brain/-/blob/feat/sub-domain-route-finder/packages-biz/by-route-finder/src/index.js#L16-26
// e.g. [bybit.com, bybit.com@1, bybit.com@2]
export const DOMAIN_LIST_API = (() => {
    const list = [CURRENT_DOMAIN, `${CURRENT_DOMAIN}@1`, `${CURRENT_DOMAIN}@2`];
    if (ENV === 'testnet' || ENV === 'prd') {
        return list;
    }
    return [list[0]];
})();

/**
 * 接口域名前缀
 *
 * @description
 *  `SFE_PROXY_ENV` -> 根据 `SFE_PROXY_ENV` 的值处理：
 *      - prd -> api2.
 *      - testnet -> api2-testnet.
 *      - unify-test-1 -> api2.unify-test-1.
 *  www.bybit.com -> api2.
 *  m.bybit.com -> api2.
 *  testnet.bybit.com -> api2-testnet.
 *  m-testnet.bybit.com -> api2-testnet.
 *  www.unify-test-1.bybit.com -> api2.unify-test-1
 *  www.dev-1.bybit.com -> api2.dev-1
 */
export const API2_SERVICE_PREFIX = (() => {
    if (ENV === 'prd') return 'api2.';
    if (ENV === 'testnet') return 'api2-testnet.';
    return `api2.${ENV}.`;
})();

/**
 * 接口域名前缀-cookie改造中的新域名
 *
 * @param {string} domain 域名
 * @description
 * Cookie改造后，api接口域名只有当前主站的域名，子域名发生改变，测试环境只有一条链路，按照以下处理：
 *  `SFE_PROXY_ENV` -> 根据 `SFE_PROXY_ENV` 的值处理：
 *      - prd -> api2. || api2-1 || api2-2
 *      - testnet -> api2-testnet. || api2-1-testnet. || api2-2-testnet.
 *      - unify-test-1 -> api2.unify-test-1.
 *  www.bybit.com -> api2.
 *  m.bybit.com -> api2.
 *  testnet.bybit.com -> api2-testnet.
 *  m-testnet.bybit.com -> api2-testnet.
 *  www.unify-test-1.bybit.com -> api2.unify-test-1
 *  www.dev-1.bybit.com -> api2.dev-1
 */
export const getApi2ServicePrefix = (domain = ls.get(ROUTE_CHOSEN_STORAGE_KEY)) => {
    const lineId = domain?.split('@')?.[1];
    const prefix = lineId ? `-${lineId}` : '';
    if (ENV === 'prd') return `api2${prefix}.`;
    if (ENV === 'testnet') return `api2${prefix}-testnet.`;
    return `api2.${ENV}.`;
};

/**
 * ws接口域名前缀
 *
 * @description
 *  `SFE_PROXY_ENV` -> 根据 `SFE_PROXY_ENV` 的值处理：
 *      - prd -> ws2.
 *      - testnet -> ws2-testnet.
 *      - unify-test-1 -> ws2.unify-test-1.
 *  www.bybit.com -> ws2.
 *  m.bybit.com -> ws2.
 *  testnet.bybit.com -> ws2-testnet.
 *  m-testnet.bybit.com -> ws2-testnet.
 *  www.unify-test-1.bybit.com -> ws2.unify-test-1
 *  www.dev-1.bybit.com -> ws2.dev-1
 */
export const WS2_SERVICE_PREFIX = (() => {
    if (ENV === 'prd') return 'ws2.';
    if (ENV === 'testnet') return 'ws2-testnet.';
    return `ws2.${ENV}.`;
})();

/**
 * 获取接口顶级域名
 *
 * @return {string}
 */
export const getApiDomain = () => {
    // TODO: Cookie改造，api接口取当前主域，ws保持不变（本期修改后已失效），待hats高速通道恢复后，改逻辑会根据线路是否变动，再做修改；
    const routeChosenCache = ls.get(ROUTE_CHOSEN_STORAGE_KEY);
    if (DOMAIN_LIST_API.includes(routeChosenCache)) return routeChosenCache?.split('@')?.[0];
    return CURRENT_DOMAIN;
};

/**
 * 获取x-api开关
 *
 * @param {string} type api或者ws
 * @return {boolean}
 */
export const getXApiSwitch = (type) => {
    // const xApiSwitch = ls.get('fe.crossApi');
    if (type === 'api' && process.env.NODE_ENV !== 'development') {
        return true;
    }
    return false;
};
/**
 * 获取接口 Origin
 *
 * @param {Object} options 配置参数
 * @param {string} [options.type='api'] 枚举值，api 、ws
 * @param {string} [options.domain] 域名
 * @return {string}
 */
export const getOrigin = ({ type = 'api', domain } = {}) => {
    if (getXApiSwitch(type)) {
        return '/x-api';
    }
    const main = (domain || getApiDomain())?.split('@')?.[0];
    const mapping = {
        api: ['http', 'https'],
        ws: ['ws', 'wss'],
    };
    const schema = mapping[type] ? mapping[type][isSSL ? 1 : 0] : mapping.api[isSSL ? 1 : 0];
    const prefix = type === 'ws' ? WS2_SERVICE_PREFIX : getApi2ServicePrefix(domain);

    return `${schema}://${prefix}${main}`;
};

/**
 * ws接口域名列表
 *
 * @description
 *  `SFE_PROXY_ENV` -> 根据 `SFE_PROXY_ENV` 的值处理：
 *      - prd -> ws2.bybit.com | ws2.bycbe.com | ws2.byapis.com
 *      - testnet -> ws2-testnet.bybit.com | ws2-testnet.bybitglobal.com
 *      - unify-test-1 -> ws2.unify-test-1.bybit.com
 *  www.bybit.com -> ws2.bybit.com
 *  www.bytick.com -> ws2.bytick.com
 *  www.bybitglobal.com -> ws2.bybitglobal.com
 *  m.bybit.com -> ws2.bybit.com
 *  testnet.bybit.com -> ws2-testnet.bybit.com
 *  m-testnet.bybit.com -> ws2-testnet.bybit.com
 *  www.unify-test-1.bybit.com -> ws2.unify-test-1.bybit.com
 *  www.dev-1.bybit.com -> ws2.dev-1.bybit.com
 */
export const WS_DOMAIN_LIST = (() => {
    const list = [];
    DOMAIN_LIST.forEach((domain) => {
        list.push(getOrigin({ type: 'ws', domain }));
    });
    return list;
})();

/**
 * 循环获取Origin List
 *
 * @param {string} id 调用方法的id，用来缓存循环执行的次数
 * @param {Object} options 配置参数
 * @param {Array} options.originList 域名列表
 * @param {string} options.currentOrigin 当前域名
 * @param {number} options.maxLoops 循环的最大次数
 * @return {Object}
 */
export const getUsableOrigin = (id, { originList = [], currentOrigin = '', maxLoops = 5 }) => {
    // 获取缓存
    let cache = ram.get(id);
    if (!cache) {
        cache = {
            times: 0, // 链路列表循环次数
            initIndex: null, // 当前链路在链路列表中的初始位置索引
        };
        if (id) {
            ram.set(id, cache);
        }
    }

    let { times, initIndex } = cache;

    // 返回值 函数
    const getValue = (isLoop = true, origin) => ({
        isLoop,
        origin,
    });

    // 没有id || 没有链路列表 || 链路列表只有一个且和当前传入的链路相同 || 循环次数 > 最大循环次数 时，返回当前传入的链路，停止循环切换新链路
    if (!id || !originList?.length || (originList?.length === 1 && originList?.[0] === currentOrigin) || times > maxLoops) {
        return getValue(false, currentOrigin);
    }

    // 新的链路
    let origin = '';

    // 获取当前链路在链路列表中的位置索引
    const index = originList.findIndex((v) => v === currentOrigin);

    // 如果 当前索引 = 初始索引，说明已经循环过一次当前链路列表，缓存中记录一次；
    if (index === initIndex) {
        times += 1;
        ram.set(id, {
            ...cache,
            times,
        });
    }

    // 如果没有缓存过，设置初始索引
    if (initIndex === null) {
        initIndex = index === -1 ? originList.length - 1 : index;
        ram.set(id, {
            ...cache,
            initIndex,
        });
    }

    if (times > maxLoops) {
        // 大于最大循环次数，取当前传入的origin
        origin = currentOrigin;
    } else if (index === originList.length - 1) {
        // 如果是最后一条，取第一条
        [origin] = originList;
    } else {
        // 循环获取
        origin = originList[index + 1];
    }

    return getValue(times < maxLoops, origin);
};

export const ORDER_LIMIT = 50;

export const isProdOrTestnet = () => window.sfe.env === 'prd' || window.sfe.env === 'testnet';
